import { Type } from "lucide-react";
import React from "react";
import { TextRotate } from "@/components/ui/text-rotate";

// Komponent podglądu
export const TextRotatePreview = () => (
  <div className="h-8 text-lg font-medium">
    I'm a <TextRotate texts={["developer", "designer", "creator"]} />
  </div>
);

const component = {
  id: "text-rotate",
  title: "Text Rotate",
  component: TextRotate,
  description: "A component that animates text rotation with various effects.",
  preview: TextRotatePreview,
  icon: <Type className="h-4 w-4 mr-2" />,
  code: `import { TextRotate } from "@/components/ui/text-rotate";

export const TextRotateExample = () => {
  return (
    <div className="text-lg font-medium">
      I'm a <TextRotate texts={["developer", "designer", "creator"]} />
    </div>
  );
};`,
  installation: "This component is already included in the project.",
  detailedInstallation: `
### Installation Steps

1. This component is part of the core UI library in the project.
2. If you're using this in a new project, copy the following files:
   - \`src/components/ui/text-rotate.jsx\`
   - Make sure you have \`framer-motion\` installed in your dependencies

### Dependencies

\`\`\`json
{
  "dependencies": {
    "framer-motion": "^10.12.16",
    "react": "^18.2.0",
    "react-dom": "^18.2.0"
  }
}
\`\`\`
`,
  sourceCode: `import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { cn } from "@/lib/utils";

const TextRotate = forwardRef(
  (
    {
      texts,
      transition = { type: "spring", damping: 30, stiffness: 300 },
      initial = { opacity: 0, y: 10 },
      animate = { opacity: 1, y: 0 },
      exit = { opacity: 0, y: -10 },
      animatePresenceMode = "wait",
      animatePresenceInitial = false,
      rotationInterval = 3000,
      staggerDuration = 0.05,
      staggerFrom = "first",
      loop = true,
      auto = true,
      splitBy = "characters",
      onNext,
      mainClassName,
      splitLevelClassName,
      elementLevelClassName,
      containerClassName,
      ...props
    },
    ref
  ) => {
    const [currentTextIndex, setCurrentTextIndex] = useState(0);
    const localRef = useRef(null);
    const isInView = useInView(localRef, { once: true });

    const splitIntoCharacters = (text) => {
      if (typeof Intl !== "undefined" && "Segmenter" in Intl) {
        return Array.from(
          new Intl.Segmenter("en", { granularity: "grapheme" }).segment(text),
          ({ segment }) => segment
        );
      }
      return Array.from(text);
    };

    const elements = useMemo(() => {
      const currentText = texts[currentTextIndex];
      if (splitBy === "characters") {
        const text = currentText.split(" ");
        return text.map((word, i) => ({
          characters: splitIntoCharacters(word),
          needsSpace: i !== text.length - 1,
        }));
      }
      return splitBy === "words"
        ? currentText.split(" ")
        : splitBy === "lines"
        ? currentText.split("\\n")
        : currentText.split(splitBy);
    }, [texts, currentTextIndex, splitBy]);

    const getStaggerDelay = useCallback(
      (index, totalChars) => {
        if (staggerFrom === "first") return index * staggerDuration;
        if (staggerFrom === "last")
          return (totalChars - 1 - index) * staggerDuration;
        if (staggerFrom === "center")
          return Math.abs(Math.floor(totalChars / 2) - index) * staggerDuration;
        if (staggerFrom === "random")
          return (
            Math.abs(Math.floor(Math.random() * totalChars) - index) *
            staggerDuration
          );
        return Math.abs(staggerFrom - index) * staggerDuration;
      },
      [staggerFrom, staggerDuration]
    );

    const handleIndexChange = useCallback(
      (newIndex) => {
        if (newIndex !== currentTextIndex) {
          setCurrentTextIndex(newIndex);
          onNext?.(newIndex);
        }
      },
      [currentTextIndex, onNext]
    );

    const next = useCallback(
      () => handleIndexChange((currentTextIndex + 1) % texts.length),
      [currentTextIndex, texts.length, handleIndexChange]
    );

    const previous = useCallback(
      () =>
        handleIndexChange((currentTextIndex - 1 + texts.length) % texts.length),
      [currentTextIndex, texts.length, handleIndexChange]
    );

    const jumpTo = useCallback(
      (index) =>
        handleIndexChange(Math.max(0, Math.min(index, texts.length - 1))),
      [texts.length, handleIndexChange]
    );

    const reset = useCallback(() => handleIndexChange(0), [handleIndexChange]);

    useImperativeHandle(ref, () => ({ next, previous, jumpTo, reset }), [
      next,
      previous,
      jumpTo,
      reset,
    ]);

    useEffect(() => {
      if (!auto) return;
      const intervalId = setInterval(next, rotationInterval);
      return () => clearInterval(intervalId);
    }, [next, rotationInterval, auto]);

    return (
      <motion.span
        className={cn("flex flex-wrap whitespace-pre-wrap", mainClassName)}
        {...props}
        layout
        transition={transition}
      >
        <span className="sr-only">{texts[currentTextIndex]}</span>
        <AnimatePresence
          mode={animatePresenceMode}
          initial={animatePresenceInitial}
        >
          <motion.div
            key={currentTextIndex}
            className={cn(
              "flex flex-wrap", // Domyślne klasy
              splitBy === "lines" && "flex-col w-full gap-0",
              splitBy === "words" && "gap-1.5",
              containerClassName // Możliwość nadpisania
            )}
            layout
            aria-hidden="true"
          >
            {elements.map((wordObj, wordIndex, array) => {
              const previousCharsCount = array
                .slice(0, wordIndex)
                .reduce((sum, word) => sum + (word.characters?.length || 1), 0);
              return (
                <span
                  key={wordIndex}
                  className={cn("inline-flex", splitLevelClassName)}
                >
                  {(wordObj.characters || [wordObj]).map((char, charIndex) => (
                    <motion.span
                      ref={localRef}
                      initial={initial}
                      animate={isInView ? animate : initial}
                      exit={exit}
                      key={charIndex}
                      transition={{
                        ...transition,
                        delay: getStaggerDelay(
                          previousCharsCount + charIndex,
                          array.reduce(
                            (sum, word) => sum + (word.characters?.length || 1),
                            0
                          )
                        ),
                      }}
                      className={cn("inline-block", elementLevelClassName)}
                    >
                      {char}
                    </motion.span>
                  ))}
                  {wordObj.needsSpace && (
                    <span className="whitespace-pre"> </span>
                  )}
                </span>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </motion.span>
    );
  }
);

TextRotate.displayName = "TextRotate";`,
  props: [
    {
      name: "texts",
      type: "string[]",
      default: "[]",
      description: "Array of strings to cycle through.",
    },
    {
      name: "transition",
      type: "object",
      default: "{ type: 'spring', damping: 30, stiffness: 300 }",
      description: "Framer Motion transition properties for the animation.",
    },
    {
      name: "initial",
      type: "object",
      default: "{ opacity: 0, y: 10 }",
      description: "Initial animation state for elements.",
    },
    {
      name: "animate",
      type: "object",
      default: "{ opacity: 1, y: 0 }",
      description: "Target animation state for elements.",
    },
    {
      name: "exit",
      type: "object",
      default: "{ opacity: 0, y: -10 }",
      description: "Exit animation state for elements.",
    },
    {
      name: "rotationInterval",
      type: "number",
      default: "3000",
      description: "Time in milliseconds before switching to the next text.",
    },
    {
      name: "staggerDuration",
      type: "number",
      default: "0.05",
      description: "Delay between each character animation in seconds.",
    },
    {
      name: "staggerFrom",
      type: "string",
      default: "'first'",
      description:
        "Direction of staggered animation ('first', 'last', 'center', 'random').",
    },
    {
      name: "loop",
      type: "boolean",
      default: "true",
      description: "Whether the animation should loop continuously.",
    },
    {
      name: "auto",
      type: "boolean",
      default: "true",
      description: "Whether the animation should play automatically.",
    },
    {
      name: "splitBy",
      type: "string",
      default: "'characters'",
      description: "How to split the text ('characters', 'words', 'lines').",
    },
  ],
  examples: [
    {
      title: "Basic usage",
      code: `<TextRotate texts={["First", "Second", "Third"]} />`,
    },
    {
      title: "In a sentence",
      code: `<div>
  We build amazing <TextRotate texts={["websites", "applications", "experiences"]} />
</div>`,
    },
    {
      title: "With custom timing",
      code: `<TextRotate 
  texts={["Fast", "Reliable", "Modern"]} 
  rotationInterval={2000} 
/>`,
    },
    {
      title: "With custom stagger",
      code: `<TextRotate 
  texts={["Animated", "Staggered", "Text"]} 
  staggerFrom="center"
  staggerDuration={0.08}
/>`,
    },
  ],
};

export default component;
